import { useState, useEffect } from "react";
import { Star, Send, TrendingUp, Users, RefreshCw, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const StarRating = ({ rating, size = "sm" }) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  
  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(
        <Star key={i} className={`fill-amber-400 text-amber-400 ${size === "sm" ? "h-3.5 w-3.5" : "h-5 w-5"}`} />
      );
    } else if (i === fullStars && hasHalf) {
      stars.push(
        <Star key={i} className={`fill-amber-400/50 text-amber-400 ${size === "sm" ? "h-3.5 w-3.5" : "h-5 w-5"}`} />
      );
    } else {
      stars.push(
        <Star key={i} className={`text-gray-300 ${size === "sm" ? "h-3.5 w-3.5" : "h-5 w-5"}`} />
      );
    }
  }
  return <div className="flex gap-0.5">{stars}</div>;
};

export default function SurveyStatsWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.getSurveyStats(4);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch survey stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendBatch = async () => {
    setSending(true);
    try {
      const res = await api.sendSurveyBatch(5);
      if (res.data.sent_count > 0) {
        toast.success(`Sent ${res.data.sent_count} survey(s)`);
      } else {
        toast.info(res.data.message || "No eligible tickets found");
      }
      fetchStats();
    } catch (err) {
      toast.error("Failed to send surveys");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="corp-card animate-fade-slide-up" style={{ animationDelay: "700ms" }}>
        <div className="h-1 w-full bg-amber-500" />
        <div className="p-6 flex items-center justify-center">
          <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const hasData = stats && stats.overall_average > 0;

  return (
    <div className="corp-card animate-fade-slide-up" style={{ animationDelay: "700ms" }} data-testid="survey-stats-widget">
      <div className="h-1 w-full bg-amber-500" />
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-slate-800">Service Surveys</h3>
            <p className="text-xs text-slate-400 mt-0.5">Customer satisfaction</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1"
            onClick={handleSendBatch}
            disabled={sending}
            data-testid="send-surveys-btn"
          >
            {sending ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            Send Batch
          </Button>
        </div>

        {hasData ? (
          <>
            {/* Overall Score */}
            <div className="flex items-center justify-between mb-4 p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
              <div>
                <div className="text-2xl font-bold text-amber-600">{stats.overall_average.toFixed(1)}</div>
                <div className="text-xs text-slate-500">Overall Rating</div>
              </div>
              <StarRating rating={stats.overall_average} size="lg" />
            </div>

            {/* Response Rate */}
            <div className="flex items-center gap-3 mb-4 text-sm">
              <div className="flex items-center gap-1.5 text-slate-600">
                <Users className="h-4 w-4" />
                <span>{stats.total_with_responses}/{stats.total_surveys_sent} responses</span>
              </div>
              <div className="flex items-center gap-1.5 text-green-600">
                <TrendingUp className="h-4 w-4" />
                <span>{stats.response_rate}% rate</span>
              </div>
            </div>

            {/* Metrics Breakdown */}
            {expanded && stats.metrics && (
              <div className="space-y-2 mb-3 pt-3 border-t">
                {Object.entries(stats.metrics).map(([key, metric]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{metric.label}</span>
                    <div className="flex items-center gap-2">
                      <StarRating rating={metric.avg_rating} />
                      <span className="text-sm font-medium text-slate-700 w-8 text-right">
                        {metric.avg_rating.toFixed(1)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Toggle Details */}
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-amber-600 transition-colors"
            >
              <ChevronRight className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-90" : ""}`} />
              {expanded ? "Hide Details" : "Show Breakdown"}
            </button>
          </>
        ) : (
          <div className="text-center py-4">
            <Star className="h-10 w-10 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No survey responses yet</p>
            <p className="text-xs text-slate-400 mt-1">Click "Send Batch" to start collecting feedback</p>
          </div>
        )}
      </div>
    </div>
  );
}
